import Image from "next/image";
import { storyblokEditable } from "@storyblok/react/rsc";

import { MaskShape, type MaskShapeVariant } from "@/components/illustrations/MaskShape";
import { RichTextField } from "@/components/typography/RichTextField";
import { cn } from "@/lib/utils";

type QuoteCardShapeColor = "sun-100" | "sand-100" | "sky-50";

const shapeColorClasses: Record<QuoteCardShapeColor, string> = {
  "sun-100": "text-sun-100",
  "sand-100": "text-sand-100",
  "sky-50": "text-sky-50",
};

type QuoteCardStoryblok = {
  name?: string;
  text?: string;
  image?: { filename?: string; alt?: string };
  shape?: MaskShapeVariant;
  shape_color?: QuoteCardShapeColor;
  _uid: string;
  component: "quote_card";
  [k: string]: any;
};

type QuoteCardProps = {
  blok: QuoteCardStoryblok;
};

const QuoteCard = ({ blok }: QuoteCardProps) => {
  const shape = (blok.shape || "four_vertical_even") as MaskShapeVariant;
  const shapeColor = (blok.shape_color || "sun-100") as QuoteCardShapeColor;
  const colorClass = shapeColorClasses[shapeColor] || "text-sun-100";

  return (
    <div
      className="relative isolate flex flex-col items-center justify-end overflow-hidden rounded-2xl w-[280px] md:w-[320px] pt-[120px] h-full"
      {...storyblokEditable(blok)}
    >
      {/* Background image */}
      {blok.image?.filename && (
        <Image
          src={blok.image.filename}
          alt={blok.image.alt || blok.name || ""}
          fill
          sizes="(min-width: 768px) 320px, 280px"
          className="object-cover pointer-events-none rounded-2xl"
        />
      )}

      {/* Shape overlay + text content */}
      <div className="relative w-full z-[1]">
        {/* Shape SVG positioned behind text */}
        <div className="absolute inset-0 z-[1]">
          <MaskShape
            variant={shape}
            className={cn("w-full h-full", colorClass)}
            preserveAspectRatio="none"
          />
        </div>

        {/* Text content */}
        <div className="relative z-[2] flex flex-col gap-4 items-center justify-center p-10 text-center text-lg leading-[1.2]">
          {blok.name && (
            <span className="font-bold w-full">{blok.name}</span>
          )}
          {blok.text && (
            <RichTextField value={blok.text} className="w-full" />
          )}
        </div>
      </div>
    </div>
  );
};

export default QuoteCard;
